import React, { useState, useMemo, useEffect, useRef } from "react";
import type { Task, TaskTemplate, RoomStatus } from "../types/types";
import Switch from "@mui/material/Switch";
import { StatusDropdown } from "./TaskItem.tsx";
import {
  PaletteColorHex,
  PaletteColorDarkHex,
  PaletteColorBg,
  PaletteColorDarkBg,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import AddIcon from "@mui/icons-material/Add";
import { isoToYmd } from "../utils/date.ts";
import TaskIcon from "@mui/icons-material/Task";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

// --- Propsの型定義 ---
interface TaskMapViewProps {
  tasks: Task[];
  onSelectDetail: (parent: Task, child?: Task) => void;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  onAddSubtask: (parentTaskId: string, title: string) => Promise<string | null>;
  reorderSubtasks?: (
    parentTaskId: string,
    subtaskIds: string[],
  ) => Promise<void>;
  roomId: number;
  honbuId: number;
  refreshTodos?: () => void;
  roomStatuses: RoomStatus[];
}

// --- 進捗バーコンポーネント ---
const ProgressBar = ({
  value,
  max,
  isDarkMode,
}: {
  value: number;
  max: number;
  isDarkMode?: boolean;
}) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  return (
    <div
      className={`w-full rounded-full h-2 overflow-hidden ${
        isDarkMode ? "bg-gray-700" : "bg-gray-200"
      }`}
    >
      <div
        className={`h-2 rounded-full transition-all duration-300 ${
          isDarkMode ? "bg-blue-400" : "bg-blue-500"
        }`}
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  );
};

// --- サブタスク行コンポーネント ---
const SubtaskRow = ({
  subtask,
  parentTask,
  onSelectDetail,
  onUpdateStatus,
  roomStatuses,
  isDarkMode,
  containerWidth,
}: {
  subtask: Task;
  parentTask: Task;
  onSelectDetail: (parent: Task, child?: Task) => void;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  roomStatuses: RoomStatus[];
  isDarkMode?: boolean;
  containerWidth: number;
}) => {
  // 横幅に応じた表示モードを決定
  // モード1（全部表示）: 800px以上
  // モード2（タイトル省略）: 600px以上800px未満
  // モード3（タイトル＆期限・担当者省略）: 600px未満
  const displayMode =
    containerWidth >= 800
      ? "full"
      : containerWidth >= 600
        ? "medium"
        : "compact";

  const assigneesText =
    subtask.completed && subtask.completedBy
      ? `${subtask.completedBy.name || "情報なし"}が完了`
      : (subtask.assignees || []).map((a) => a.name).join(", ") || "未設定";

  const dateText =
    subtask.completed && subtask.completedAt
      ? `${isoToYmd(subtask.completedAt)}に完了`
      : subtask.startDate
        ? subtask.startDate.slice(0, 10)
        : "未設定";

  return (
    <div
      className={`flex items-center gap-3 p-2 border-b last:border-b-0 cursor-pointer ${
        isDarkMode
          ? "hover:bg-gray-700 border-gray-700"
          : "hover:bg-gray-100 border-gray-200"
      }`}
      onClick={() => onSelectDetail(parentTask, subtask)}
    >
      <div className="w-24 flex-shrink-0">
        <StatusDropdown
          task={subtask}
          onUpdate={onUpdateStatus}
          isMobile={false}
          roomStatuses={roomStatuses}
        />
      </div>
      {(() => {
        // サブタスクのステータス色を取得
        const subtaskStatusInfo = roomStatuses.find(
          (s) => s.statusName === subtask.status,
        );
        const subtaskTitleColor = subtaskStatusInfo
          ? subtaskStatusInfo.color === "Gray"
            ? isDarkMode
              ? "#e5e7eb"
              : "#374151"
            : isDarkMode
              ? PaletteColorDarkHex[subtaskStatusInfo.color]
              : PaletteColorHex[subtaskStatusInfo.color]
          : isDarkMode
            ? "#e5e7eb"
            : "#374151";

        return (
          <div
            className={`text-sm font-medium ${
              displayMode === "full"
                ? "flex-grow"
                : displayMode === "medium"
                  ? "flex-grow min-w-0"
                  : "flex-grow min-w-0"
            }`}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: subtaskTitleColor,
              textDecoration: subtask.completed ? "line-through" : "none",
            }}
          >
            {subtask.title}
          </div>
        );
      })()}
      {displayMode !== "compact" && (
        <>
          <div
            className={`${
              displayMode === "full" ? "w-24" : "w-20"
            } flex-shrink-0 text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {assigneesText}
          </div>
          <div
            className={`${
              displayMode === "full" ? "w-24" : "w-20"
            } flex-shrink-0 text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {dateText}
          </div>
        </>
      )}
    </div>
  );
};

// --- TaskMapView本体 ---
export const TaskMapView: React.FC<TaskMapViewProps> = ({
  tasks,
  onSelectDetail,
  onUpdateStatus,
  onAddSubtask,
  reorderSubtasks,
  roomId,
  honbuId,
  refreshTodos,
  roomStatuses,
}) => {
  const isDarkMode = useDarkMode();
  const [showOnlyWithSubtasks, setShowOnlyWithSubtasks] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [addingSubtaskTo, setAddingSubtaskTo] = useState<string | null>(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [templateMenuFor, setTemplateMenuFor] = useState<{
    id: string;
    target: HTMLButtonElement;
  } | null>(null);
  const [subtaskTemplates, setSubtaskTemplates] = useState<TaskTemplate[]>([]);
  const newSubtaskInputRef = useRef<HTMLInputElement>(null);
  const [templatePosition, setTemplatePosition] = useState<"top" | "bottom">(
    "top",
  );
  const templateMenuRef = useRef<HTMLDivElement>(null);

  // コンテナ幅を監視
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const filteredParentTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (showOnlyWithSubtasks && (task.childTasks || []).length === 0)
        return false;
      return true;
    });
  }, [tasks, showOnlyWithSubtasks]);

  const handleCreateSubtask = async (parentTaskId: string) => {
    const trimmedTitle = newSubtaskTitle.trim();
    if (trimmedTitle) {
      await onAddSubtask(parentTaskId, trimmedTitle);
    }
    setAddingSubtaskTo(null);
    setNewSubtaskTitle("");
  };

  const handleNewSubtaskKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    parentTaskId: string,
  ) => {
    if (e.key === "Enter") handleCreateSubtask(parentTaskId);
    else if (e.key === "Escape") {
      setAddingSubtaskTo(null);
      setNewSubtaskTitle("");
    }
  };

  const handleDragEnd = (result: DropResult, parentTaskId: string) => {
    if (!result.destination || !reorderSubtasks) return;

    const parentTask = tasks.find((t) => t.id === parentTaskId);
    if (!parentTask || !parentTask.childTasks) return;

    const items = Array.from(parentTask.childTasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const subtaskIds = items.map((item) => item.id);
    reorderSubtasks(parentTaskId, subtaskIds);
  };

  useEffect(() => {
    if (addingSubtaskTo) {
      newSubtaskInputRef.current?.focus();
    }
  }, [addingSubtaskTo]);

  const handleApplySubtaskTemplate = async (
    parentTaskId: string,
    template: TaskTemplate,
  ) => {
    const taskData = JSON.parse(template.taskData || "{}");
    const childTasks = taskData.childTasks || [];
    const tasksToCreate = childTasks.length > 0 ? childTasks : [taskData];

    try {
      for (const subtask of tasksToCreate) {
        const payload = {
          title: subtask.title || "テンプレートから作成",
        };

        const response = await fetch(`/api/tasks/${parentTaskId}/subtasks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error("Failed to create subtask");
        }
      }

      if (refreshTodos) {
        await refreshTodos();
      }
    } catch (error) {
      console.error("Failed to create subtasks from template:", error);
      alert("サブタスクの作成に失敗しました。");
    } finally {
      setTemplateMenuFor(null);
    }
  };

  useEffect(() => {
    if (templateMenuFor) {
      // templateMenuFor がオブジェクトになった
      const rect = templateMenuFor.target.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow > 240 || spaceBelow > spaceAbove) {
        setTemplatePosition("bottom");
      } else {
        setTemplatePosition("top");
      }
    }
  }, [templateMenuFor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target as Node) &&
        templateMenuFor &&
        !templateMenuFor.target.contains(event.target as Node)
      ) {
        setTemplateMenuFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [templateMenuFor]);

  useEffect(() => {
    if (templateMenuFor) {
      const fetchSubtaskTemplates = async () => {
        try {
          const res = await fetch(
            `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
          );
          if (res.ok) setSubtaskTemplates(await res.json());
        } catch (error) {
          console.error("Failed to fetch subtask templates:", error);
        }
      };
      fetchSubtaskTemplates();
    }
  }, [templateMenuFor, roomId, honbuId]);

  return (
    <div
      className={`h-full flex flex-col ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      {/* --- ヘッダーコントロール部分 --- */}
      <div
        className={`flex-shrink-0 pr-2 pb-2 border-b flex items-center justify-end gap-4 sticky top-0 z-10 ${
          isDarkMode
            ? "border-gray-700 bg-gray-900"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        <label className="flex items-center cursor-pointer select-none">
          <Switch
            size="small"
            checked={showOnlyWithSubtasks}
            onChange={(e) => setShowOnlyWithSubtasks(e.target.checked)}
          />
          <span
            className={`text-sm ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            サブタスクのあるタスクのみ表示
          </span>
        </label>
      </div>

      {/* --- タスク一覧 --- */}
      <div
        ref={containerRef}
        className="flex-grow overflow-y-auto"
        onClick={() => {
          if (templateMenuFor) setTemplateMenuFor(null);
        }}
      >
        <div className="p-4 space-y-4">
          {filteredParentTasks.map((parentTask) => {
            const total = (parentTask.childTasks || []).length;
            const completed = (parentTask.childTasks || []).filter(
              (t) => t.status === "完了",
            ).length;

            return (
              <div
                key={parentTask.id}
                data-task-id={parentTask.id}
                className={`rounded-lg shadow-md border ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="grid grid-cols-[300px_1fr] items-stretch">
                  <div
                    className={`p-4 border-r cursor-pointer flex flex-col justify-center gap-2 ${
                      isDarkMode
                        ? "bg-gray-800 hover:bg-gray-700 border-gray-700"
                        : "bg-gray-50 hover:bg-gray-100 border-gray-200"
                    }`}
                    onClick={() => onSelectDetail(parentTask)}
                  >
                    {(() => {
                      // 親タスクのステータス色を取得
                      const parentStatusInfo = roomStatuses.find(
                        (s) => s.statusName === parentTask.status,
                      );
                      const parentTitleColor = parentStatusInfo
                        ? parentStatusInfo.color === "Gray"
                          ? isDarkMode
                            ? "#f3f4f6"
                            : "#1f2937"
                          : isDarkMode
                            ? PaletteColorDarkHex[parentStatusInfo.color]
                            : PaletteColorHex[parentStatusInfo.color]
                        : isDarkMode
                          ? "#f3f4f6"
                          : "#1f2937";

                      return (
                        <h3
                          className="font-bold"
                          style={{
                            color: parentTitleColor,
                            textDecoration: parentTask.completed
                              ? "line-through"
                              : "none",
                          }}
                        >
                          {parentTask.title}
                        </h3>
                      );
                    })()}
                    {total > 0 && (
                      <div className="space-y-1">
                        <div
                          className={`flex justify-between items-center text-xs ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          <span>進捗</span>
                          <span>
                            {completed} / {total}
                          </span>
                        </div>
                        <ProgressBar
                          value={completed}
                          max={total}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col">
                    {(parentTask.childTasks || []).length > 0 ? (
                      <DragDropContext
                        onDragEnd={(result) =>
                          handleDragEnd(result, parentTask.id)
                        }
                      >
                        <Droppable droppableId={`subtasks-${parentTask.id}`}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {(parentTask.childTasks || []).map(
                                (subtask, index) => (
                                  <Draggable
                                    key={subtask.id}
                                    draggableId={subtask.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={
                                          snapshot.isDragging
                                            ? "bg-blue-50 shadow-lg"
                                            : ""
                                        }
                                      >
                                        <div className="flex items-center">
                                          <div
                                            {...provided.dragHandleProps}
                                            className="flex-shrink-0 cursor-grab active:cursor-grabbing p-2"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <DragIndicatorIcon className="text-gray-400 w-5 h-5" />
                                          </div>
                                          <div className="flex-1">
                                            <SubtaskRow
                                              subtask={subtask}
                                              parentTask={parentTask}
                                              onSelectDetail={onSelectDetail}
                                              onUpdateStatus={onUpdateStatus}
                                              roomStatuses={roomStatuses}
                                              isDarkMode={isDarkMode}
                                              containerWidth={containerWidth}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                ),
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    ) : (
                      <div
                        className={`flex items-center justify-center h-full text-sm p-4 ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        サブタスクはありません
                      </div>
                    )}

                    {addingSubtaskTo === parentTask.id ? (
                      <div
                        className={`p-2 border-t ${
                          isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <input
                          ref={newSubtaskInputRef}
                          type="text"
                          value={newSubtaskTitle}
                          onChange={(e) => setNewSubtaskTitle(e.target.value)}
                          onKeyDown={(e) =>
                            handleNewSubtaskKeyDown(e, parentTask.id)
                          }
                          onBlur={() => handleCreateSubtask(parentTask.id)}
                          placeholder="新規サブタスクを入力 (Enterで確定)"
                          className={`w-full px-2 py-1 border rounded focus:outline-none text-sm ${
                            isDarkMode
                              ? "bg-gray-700 border-blue-500 text-gray-200 placeholder-gray-500"
                              : "bg-white border-blue-400 text-gray-900"
                          }`}
                        />
                      </div>
                    ) : (
                      <div
                        className={`flex items-center gap-4 p-2 border-t ${
                          isDarkMode
                            ? "bg-gray-800/50 border-gray-700"
                            : "bg-gray-50/50 border-gray-200"
                        }`}
                      >
                        <button
                          className={`text-xs font-semibold hover:underline flex items-center gap-1 ${
                            isDarkMode ? "text-blue-400" : "text-blue-600"
                          }`}
                          onClick={() => setAddingSubtaskTo(parentTask.id)}
                        >
                          <AddIcon fontSize="inherit" />
                          サブタスクを追加
                        </button>
                        <div className="relative">
                          <button
                            className={`text-xs font-semibold hover:underline flex items-center gap-1 ${
                              isDarkMode ? "text-blue-400" : "text-blue-600"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateMenuFor({
                                id: parentTask.id,
                                target: e.currentTarget,
                              });
                            }}
                          >
                            <AddIcon fontSize="inherit" />
                            テンプレートから追加
                          </button>
                          {templateMenuFor?.id === parentTask.id && (
                            <div
                              ref={templateMenuRef}
                              onClick={(e) => e.stopPropagation()}
                              className={`absolute ${
                                templatePosition === "top"
                                  ? "bottom-full mb-1"
                                  : "top-full mt-1"
                              } w-60 border rounded shadow-lg z-20 max-h-60 overflow-y-auto ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-600"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              {subtaskTemplates.length > 0 ? (
                                subtaskTemplates.map((template) => {
                                  const taskData = JSON.parse(
                                    template.taskData || "{}",
                                  );
                                  const childTasks = taskData.childTasks || [];
                                  return (
                                    <button
                                      key={template.templateId}
                                      onClick={() =>
                                        handleApplySubtaskTemplate(
                                          parentTask.id,
                                          template,
                                        )
                                      }
                                      className={`w-full text-left p-3 text-sm flex flex-col gap-1 ${
                                        isDarkMode
                                          ? "hover:bg-gray-700 text-gray-200"
                                          : "hover:bg-gray-100 text-gray-900"
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {/* テンプレートタイプに応じたアイコン */}
                                        {childTasks.length > 0 ? (
                                          <ListAltIcon
                                            fontSize="small"
                                            titleAccess="マルチテンプレート"
                                          />
                                        ) : (
                                          <TaskIcon
                                            fontSize="small"
                                            titleAccess="シングルテンプレート"
                                          />
                                        )}
                                        <span className="truncate">
                                          {template.templateName}
                                        </span>
                                        {/* 共有中の表示 */}
                                        {template.isShare && (
                                          <span
                                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                              isDarkMode
                                                ? "text-blue-300 bg-blue-900"
                                                : "text-blue-700 bg-blue-100"
                                            }`}
                                          >
                                            共有中
                                          </span>
                                        )}
                                      </div>
                                      {/* サブタスク数の表示 */}
                                      {childTasks.length > 0 && (
                                        <p
                                          className={`text-xs ${
                                            isDarkMode
                                              ? "text-gray-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          {childTasks.length}
                                          件のサブタスクが作られます
                                        </p>
                                      )}
                                    </button>
                                  );
                                })
                              ) : (
                                <p
                                  className={`p-3 text-sm ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-500"
                                  }`}
                                >
                                  利用可能なテンプレートはありません。
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
